(() => {
  const elementName = 'hf-delivery-estimator';
  const londonTimeZone = 'Europe/London';


  // GOV.UK bank-holiday calendars checked 2026-09-13.
  // Conservative UK-wide union: destination and supplier closures are not yet known.
  // Includes regional and substitute days, but not unconfirmed supplier shutdowns.
  // Refresh when new holidays are announced; never extrapolate beyond this coverage.
  // Source: https://www.gov.uk/bank-holidays
  const holidayCoverageStart = '2026-01-01';
  const holidayCoverageEnd = '2028-12-31';
  const bankHolidays = new Set(["2026-01-01", "2026-01-02", "2026-03-17", "2026-04-03", "2026-04-06", "2026-05-04", "2026-05-25", "2026-06-15", "2026-07-13", "2026-08-03", "2026-08-31", "2026-11-30", "2026-12-25", "2026-12-28", "2027-01-01", "2027-01-04", "2027-03-17", "2027-03-26", "2027-03-29", "2027-05-03", "2027-05-31", "2027-07-12", "2027-08-02", "2027-08-30", "2027-11-30", "2027-12-27", "2027-12-28", "2028-01-03", "2028-01-04", "2028-03-17", "2028-04-14", "2028-04-17", "2028-05-01", "2028-05-29", "2028-07-12", "2028-08-07", "2028-08-28", "2028-11-30", "2028-12-25", "2028-12-26"]);

  if (customElements.get(elementName)) return;

  class HeartfeltDeliveryEstimator extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (this.productInfo) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const sectionId = this.productInfo.dataset.originalSection || this.productInfo.dataset.section;
          if (data.sectionId === sectionId) this.updateAvailability(data.variant?.available === true);
        });
        this.onVariantInputChange = (event) => {
          if (event.target.matches('input[name="id"]') && !event.target.value) this.updateAvailability(false);
        };
        this.productInfo.addEventListener('change', this.onVariantInputChange);
      }
      this.updateAvailability(this.dataset.variantAvailable === 'true');
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
      this.productInfo?.removeEventListener('change', this.onVariantInputChange);
    }

    updateAvailability(available) {
      this.dataset.variantAvailable = String(available);
      this.hidden = !available;
      if (available) this.enhanceEstimate();
    }

    enhanceEstimate() {
      if (this.initialized) return;
      this.initialized = true;

      const dispatchElement = this.querySelector('[data-del-dispatch-value]');
      const deliveryElement = this.querySelector('[data-del-delivery-value]');
      const config = this.readConfig();

      if (!dispatchElement || !deliveryElement || !config) return;

      try {
        const calendarDate = this.getLondonCalendarDate(config.serverNow, config.timeZone);
        const earliestDispatch = this.addWorkingDays(calendarDate, config.dispatchMin);
        const latestDispatch = this.addWorkingDays(calendarDate, config.dispatchMax);
        const earliestDelivery = this.addWorkingDays(earliestDispatch, config.transitMin);
        const latestDelivery = this.addWorkingDays(latestDispatch, config.transitMax);
        const dispatchValue = this.formatRange(earliestDispatch, latestDispatch);
        const deliveryValue = this.formatRange(earliestDelivery, latestDelivery);

        dispatchElement.textContent = dispatchValue;
        deliveryElement.textContent = deliveryValue;
        this.dataset.enhanced = 'true';
        this.dataset.calendarStatus = 'uk-conservative-2026-2028';
      } catch (error) {
        delete this.dataset.enhanced;
        this.dataset.calendarStatus = 'relative-fallback';
        return;
      }
    }

    readConfig() {
      const dispatchMin = this.readInteger(this.dataset.dispatchMin);
      const dispatchMax = this.readInteger(this.dataset.dispatchMax);
      const transitMin = this.readInteger(this.dataset.transitMin);
      const transitMax = this.readInteger(this.dataset.transitMax);

      if (
        dispatchMin === null ||
        dispatchMax === null ||
        transitMin === null ||
        transitMax === null ||
        !this.isSupportedDispatchRange(dispatchMin, dispatchMax) ||
        transitMin !== 2 ||
        transitMax !== 3
      ) {
        return null;
      }

      return {
        dispatchMin,
        dispatchMax,
        transitMin,
        transitMax,
        serverNow: this.readServerNow(this.dataset.serverNow),
        timeZone: this.readTimeZone(this.dataset.timeZone),
      };
    }

    isSupportedDispatchRange(minimum, maximum) {
      return (
        (minimum === 1 && maximum === 3) ||
        (minimum === 3 && maximum === 5) ||
        (minimum === 5 && maximum === 7) ||
        (minimum === 7 && maximum === 10)
      );
    }

    readInteger(value) {
      const normalized = String(value || '').trim();
      if (!/^\d+$/.test(normalized)) return null;

      const parsed = Number.parseInt(normalized, 10);
      return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
    }

    readServerNow(value) {
      const normalized = String(value || '').trim();
      const seconds = Number(normalized);
      const milliseconds = seconds * 1000;

      if (
        normalized !== '' &&
        Number.isFinite(seconds) &&
        Number.isFinite(new Date(milliseconds).getTime())
      ) {
        return milliseconds;
      }

      return Date.now();
    }

    readTimeZone(value) {
      return value === londonTimeZone ? value : londonTimeZone;
    }

    getLondonCalendarDate(instant, timeZone) {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const parts = {};

      formatter.formatToParts(new Date(instant)).forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = Number.parseInt(part.value, 10);
      });

      if (
        !Number.isInteger(parts.year) ||
        !Number.isInteger(parts.month) ||
        !Number.isInteger(parts.day)
      ) {
        throw new RangeError('Unable to derive the London calendar date');
      }

      return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    }

    addWorkingDays(date, count) {
      const result = new Date(date.getTime());
      if (!Number.isSafeInteger(count) || count < 0) throw new RangeError('Invalid working-day count');
      this.assertCalendarCoverage(result);
      let remaining = count;

      while (remaining > 0) {
        result.setUTCDate(result.getUTCDate() + 1);
        const key = this.assertCalendarCoverage(result);
        const day = result.getUTCDay();
        if (day !== 0 && day !== 6 && !bankHolidays.has(key)) remaining -= 1;
      }

      return result;
    }

    assertCalendarCoverage(date) {
      const key = date.toISOString().slice(0, 10);
      if (key < holidayCoverageStart || key > holidayCoverageEnd) {
        throw new RangeError('Bank-holiday calendar needs updating');
      }
      return key;
    }

    formatRange(start, end) {
      if (start.getTime() === end.getTime()) {
        return this.formatDate(start, false);
      }

      const crossesYear = start.getUTCFullYear() !== end.getUTCFullYear();
      return `${this.formatDate(start, crossesYear)} \u2013 ${this.formatDate(end, crossesYear)}`;
    }

    formatDate(date, includeYear) {
      return this.createDateFormatter(includeYear).format(date).replace(',', '');
    }

    createDateFormatter(includeYear) {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC',
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        ...(includeYear ? { year: 'numeric' } : {}),
      });
    }
  }

  customElements.define(elementName, HeartfeltDeliveryEstimator);
})();
